/**
 * When a composer commits, and when it may commit at all.
 *
 * ── WHY THIS IS ONE MODULE AND NOT THREE ANSWERS ─────────────────────────────
 *
 * Studio has three composers — Bot Mode's canvas input, `studio-composer.tsx`
 * and `chat-rail-input-bar.tsx` — and every one of them has to answer the same
 * two questions before a person's sentence goes anywhere:
 *
 *   1. Is THIS Enter a send, or is it the Enter that closes an IME composition?
 *   2. May this draft be sent at all?
 *
 * The two chat composers answered (1) the same way and (2) two different ways.
 * Bot Mode did not ask (1) at all: its `<input>` sat inside a `<form>`, so the
 * Enter that COMMITS a Hangul syllable triggered the browser's implicit form
 * submission and a half-typed 안 went to the agent. It answered (2) with
 * `disabled={busy}`, so the send control was live over an empty draft and
 * pressing it changed nothing on the page and said nothing.
 *
 * Both are the same defect class: a rule that lives in three private keydown
 * handlers drifts, and nothing derives one from another. The rule is HERE now,
 * and each composer keeps its own markup — which is the honest split, because
 * the two screens genuinely look different (a round icon button that turns into
 * a cancel control; a text button that says 보내기) while the rule they enforce
 * is identical.
 *
 * ── WHY BOTH COMPOSITION SIGNALS ─────────────────────────────────────────────
 *
 * `KeyboardEvent.isComposing` is the specified answer. It is also absent on
 * older Safari and on several Android IMEs, which report a composition-in-
 * progress key as `keyCode` 229 instead. Both chat composers check both; this
 * module keeps both, because dropping the legacy one would silently un-fix
 * exactly the users the guard exists for and no test in a jsdom suite would
 * notice.
 */

/** The composition-state fields a composer reads off a native keyboard event. */
export type ComposerNativeKeyState = {
  isComposing?: boolean;
  keyCode?: number;
};

/** The React keyboard event shape this rule needs — nothing more. */
export type ComposerKeyEvent = {
  key: string;
  shiftKey: boolean;
  nativeEvent: ComposerNativeKeyState;
};

/** The legacy composition keyCode, for engines that never set `isComposing`. */
const IME_COMPOSITION_KEY_CODE = 229;

/**
 * True while the IME still owns this keystroke.
 *
 * A composer that treats this Enter as a send posts a half-composed syllable —
 * the first thing a Korean-speaking user hits, on their first word.
 */
export function isComposingKeystroke(event: ComposerNativeKeyState): boolean {
  return event.isComposing === true || event.keyCode === IME_COMPOSITION_KEY_CODE;
}

/**
 * True when this keystroke means "send it now".
 *
 * Shift+Enter is a newline in every Studio composer and must never commit.
 */
export function isComposerCommitKey(event: ComposerKeyEvent): boolean {
  if (isComposingKeystroke(event.nativeEvent)) return false;
  return event.key === "Enter" && !event.shiftKey;
}

/**
 * True when the draft may be sent at all.
 *
 * `busy` is the surface's own in-flight fact — `isStartingApp` in the studio
 * composer, `isSubmitting` in the rail, a non-null `pendingSince` in Bot Mode.
 * A surface that offers its own cancel affordance while busy decides that
 * separately; this answers only whether a SEND is available.
 */
export function canCommitComposerDraft(draft: string, busy: boolean): boolean {
  return !busy && draft.trim().length > 0;
}
